import {
  Box,
  BoxProps,
  ButtonBase,
  Collapse,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  forwardRef,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useId,
  useState,
} from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Tooltip } from 'src/components/Tooltip/Tooltip';

const COLLAPSE_DURATION_MS = 200;
const COLLAPSE_EASING = 'cubic-bezier(.2,.8,.2,1)';

export type SidePanelCardProps = {
  /** Entity glyph or avatar chip rendered before the title. */
  leading?: ReactNode;
  title: ReactNode;
  /**
   * Shown on hover — the title truncates, so long ones are unreadable without it.
   * Pass a function to wrap the title node in a richer tooltip of your own.
   */
  titleTooltip?: string | ((title: ReactElement) => ReactElement);
  /** Muted second line under the title. */
  subtitle?: ReactNode;
  /** Controlled open state; omit it to let the card own its state. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Defer mounting the body until the card is first opened, then keep it mounted. */
  mountContentOnOpen?: boolean;
  /** Rendered between the title and the chevron; its clicks never toggle the card. */
  trailing?: ReactNode;
  children?: ReactNode;
} & Omit<BoxProps, 'title'>;

export const SidePanelCard = forwardRef<HTMLDivElement, SidePanelCardProps>(
  (
    {
      leading,
      title,
      titleTooltip,
      subtitle,
      open,
      defaultOpen = true,
      onOpenChange,
      mountContentOnOpen,
      trailing,
      children,
      sx,
      ...rest
    },
    ref,
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const bodyId = useId();

    const isOpen = open ?? uncontrolledOpen;

    const stopToggle = useCallback((event: MouseEvent) => {
      event.stopPropagation();
    }, []);

    const handleToggle = useCallback(() => {
      const nextOpen = !isOpen;

      if (open === undefined) {
        setUncontrolledOpen(nextOpen);
      }

      onOpenChange?.(nextOpen);
    }, [isOpen, onOpenChange, open]);

    const titleNode = (
      <Typography
        noWrap
        sx={{ fontWeight: 550, fontSize: '0.84375rem', letterSpacing: '-0.01em' }}
      >
        {title}
      </Typography>
    );

    return (
      <Box
        ref={ref}
        sx={[
          (theme) => ({
            border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
            borderRadius: '0.625rem',
            overflow: 'hidden',
            backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
          }),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- MUI sx-merge pattern
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      >
        <ButtonBase
          disableRipple
          aria-controls={bodyId}
          aria-expanded={isOpen}
          onClick={handleToggle}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '0.625rem',
            padding: '0.75rem 0.875rem',
            textAlign: 'left',
          }}
        >
          {leading}
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            {typeof titleTooltip === 'function' ? (
              titleTooltip(titleNode)
            ) : (
              <Tooltip title={titleTooltip ?? ''}>{titleNode}</Tooltip>
            )}
            {subtitle ? (
              <Box
                sx={(theme) => ({
                  minWidth: 0,
                  fontSize: '0.6875rem',
                  color: theme.palette.custom.OV.sys.onSurfaceVariant,
                })}
              >
                {subtitle}
              </Box>
            ) : null}
          </Stack>
          {trailing ? (
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
              onClick={stopToggle}
            >
              {trailing}
            </Box>
          ) : null}
          <DynamicIcon
            name="expand_more"
            sx={(theme) => ({
              flexShrink: 0,
              width: '1.125rem',
              height: '1.125rem',
              fontSize: '1.125rem',
              color: theme.palette.custom.OV.sys.onSurfaceVariant,
              transition: 'transform .15s',
              transform: isOpen ? 'none' : 'rotate(-90deg)',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            })}
          />
        </ButtonBase>
        {/* The frame stays on the card and never on the collapsing box: a border here
            would survive the collapse as a 1px hairline. Children stay mounted so the
            same node animates on every toggle. */}
        <Collapse
          id={bodyId}
          in={isOpen}
          timeout={prefersReducedMotion ? 0 : COLLAPSE_DURATION_MS}
          easing={COLLAPSE_EASING}
          mountOnEnter={mountContentOnOpen}
        >
          <Box sx={{ padding: '0 0.875rem 0.75rem' }}>
            <Box
              sx={(theme) => ({
                height: '1px',
                margin: '0.125rem 0 0.625rem',
                backgroundColor: theme.palette.custom.OV.sys.outlineVariant,
              })}
            />
            {children}
          </Box>
        </Collapse>
      </Box>
    );
  },
);

SidePanelCard.displayName = 'SidePanelCard';
